import {
  Component,
  OnChanges,
  SimpleChanges,
  OnDestroy,
  OnInit,
  AfterViewChecked,
  HostBinding,
  Input,
} from '@angular/core';
import { take } from 'rxjs';
import { LogService } from 'src/app/services/log.service';
import { TranslateService } from "@ngx-translate/core";
import { BehaviorSubject, of, Subject } from "rxjs";
import { ITapChartConfig } from "src/app/modules/charting/tap-chart.component";
import { Store } from "@ngrx/store";
import { AppState } from "src/app/app.state";
import { UtilsService } from "src/app/services/utils.service";
import { IPointRenderEventArgs } from '@syncfusion/ej2-angular-charts';
import { CHART_COLORS } from '../../../shared/Constants/sharedConstants';
import { ITankAggregatedUsageChartVM } from '../../../interfaces/tankAggregatedUsageChart';
import { UsageService } from '../../../services/usage.service';

@Component({
  selector: 'app-tank-daily-usage-chart',
  templateUrl: './tank-daily-usage-chart.component.html',
  styleUrls: ['./tank-daily-usage-chart.component.css'],
})
export class TankDailyUsageChartComponent
  implements OnChanges, OnDestroy, AfterViewChecked, OnInit {
  @Input() chartTitle: string;
  @Input() tankId: number;
  isLoaded$ = of(false);

  destroy$: Subject<boolean> = new Subject();
  seriesFillColor = this.utilsService.getSiteWideThemeDarkColorHEXValue();
  showSpinner$: BehaviorSubject<boolean> = new BehaviorSubject(false);

  legendData = [
    { key: "Avg Usage: 7 Days", color: this.seriesFillColor },
  ];

  chartConfig: ITapChartConfig = {
    chartTitle: "",
    chartHeight: '150px',
    chartWidth: '100%',
    margin: { left: 30, right: 20, top: 0, bottom: 20 },
    backgroundColor: "transparent",
    titleStyle: {
      size: "12px",
      fontFamily: "Calibre",
    },
    chartSubTitle: null,
    chartSeries: [
      {
        xName: "date",
        yName: "value",
        width: 1,
        fill: this.seriesFillColor,
        animation: {
          enable: false,
        },
        type: "Line",
        marker: {
          visible: true,
          shape: "Circle",
          width: 10,
          height: 10,
        },
      }
    ],
    primaryXAxis: {
      visible: true,
      valueType: "DateTimeCategory",
      lineStyle: {
        width: 0,
      },
      labelStyle: {
        fontWeight: "Bold",
        fontFamily: "Calibre",
        size: "12px",
      },
      labelFormat: "MM/dd",
      rangePadding: "Additional",
    },
    primaryYAxis: {
      visible: false,
      rangePadding: "Additional",
      labelFormat: "n2",
    },
    annotations: [
      {
        content: `<div style='transform: rotate(-90deg);font-weight: bold; font-size: 12pt; rbg(104, 104, 104);'>${this.translate.instant(
          'Charting.Label.TankDailyUsageChart',
        )}</div>`,
        region: 'Series',
        coordinateUnits: 'Pixel',
        horizontalAlignment: 'Center',
        verticalAlignment: 'Bottom',
        y: '50%',
      },
      {
        content: `<div style='font-weight: bold; font-size: 12pt; color:  rgb(104, 104, 104);'>${this.translate.instant(

          'Charting.Label.Dates',
        )}</div>`,
        coordinateUnits: 'Pixel',
        x: '50%',
        y: '95%',
      },
    ],
    legendSettings: {
      visible: false,
      position: 'Right',
    },
  };

  chartData: any[] | null;
  palletIndex: number;
  popOutPalletIndex: any;

  /** Same compact height while the request is in flight (chartData is still
   * unset) as once it comes back empty - there is nothing to show either way,
   * so the toolbar-plus-message state should not reserve as much room as a
   * populated chart. See tap-chart.component.ts / tap-dashboard-chart for the
   * custom property this feeds. */
  @HostBinding('style.--tl-chart-empty-min-height')
  get emptyStateMinHeight(): string | null {
    return (this.chartData?.length ?? 0) > 0 ? null : '64px';
  }

  constructor(
    private translate: TranslateService,
    private store: Store<AppState>,
    private utilsService: UtilsService,
    private _usageService: UsageService,
    private logger: LogService
  ) {}

  ngOnChanges(changes: SimpleChanges): void {}

  ngOnDestroy(): void {
    if (!this.showSpinner$.closed)
      this.showSpinner$.complete();
  }

  ngAfterViewChecked(): void {
  }

  ngOnInit() {
    this.palletIndex = 0;
    this.chartConfig.chartTitle = this.translate.instant(this.chartTitle);
    this.isLoaded$ = of(true);

    this._usageService
      .getTankDailyUsageChartDataAsync(this.tankId)
      .pipe(take(1))
      .subscribe((data) => {
        if (!data) return;

        this.bindData(data);
      });
  }

  bindData(data: ITankAggregatedUsageChartVM) {
    this.chartData = [];
    if (!data) return;

    const tempChartData = data.dataPoints.map(row => ({
      date: row.date,
      value: row.totalVolume ?? 0,
      deliveryValue: row.totalDelivery ?? 0
    }));

    this.chartData = tempChartData;
    
    this.showSpinner$.next(false);
  }

  onPointRender(arg: IPointRenderEventArgs) {
    arg.fill = CHART_COLORS[this.palletIndex];
    arg.border.color = CHART_COLORS[this.palletIndex];
    this.palletIndex = (this.palletIndex + 1) % CHART_COLORS.length;
  }

  onPopOutChartPointRender(arg: IPointRenderEventArgs) {
    arg.fill = CHART_COLORS[this.popOutPalletIndex];
    arg.border.color = CHART_COLORS[this.popOutPalletIndex];
    this.popOutPalletIndex = (this.popOutPalletIndex + 1) % CHART_COLORS.length;
  }

}
